import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  PaginationState,
  getPaginationRowModel,
  VisibilityState
} from '@tanstack/react-table';
import { getDefaultColumns, useTablePreferences } from './tableUtils';
import { cn, isSmallViewport } from "@/lib/utils";
import { FundRawRecord } from "./types";
import { PlusCircle, Star, Eye, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ResizableFundsTableProps {
  data: FundRawRecord[];
  isLoading: boolean;
  onAddToPortfolio?: (fund: FundRawRecord) => void;
  onFavorite?: (fund: FundRawRecord) => void;
  onAddToWatchlist?: (fund: FundRawRecord) => void;
  rowsPerPage?: number;
  currentPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  bulkSelection?: {
    isSelected: (id: string) => boolean;
    toggleItem: (id: string) => void;
    toggleAll: (allIds: string[]) => void;
    isAllSelected: boolean;
    isIndeterminate: boolean;
  };
}

export function ResizableFundsTable({
  data = [],
  isLoading = false,
  onAddToPortfolio,
  onFavorite,
  onAddToWatchlist,
  rowsPerPage = 100,
  currentPage = 1,
  totalCount = 0,
  onPageChange,
  bulkSelection
}: ResizableFundsTableProps) {
  // States for table
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: rowsPerPage,
  });

  // Get default columns
  const columns = useMemo(() => {
    const currentPageIds = data.map(fund => fund.id);
    const bulkSelectionConfig = bulkSelection ? {
      ...bulkSelection,
      currentPageIds
    } : undefined;
    
    const baseColumns = getDefaultColumns(bulkSelectionConfig);
    
    // Customize the actions column based on available handlers
    if (onAddToPortfolio || onFavorite || onAddToWatchlist) {
      const actionsColumn = baseColumns.find(col => col.id === 'actions');
      if (actionsColumn) {
        actionsColumn.cell = ({ row }) => {
          const fund = row.original;
          const hasHandlers = !!(onAddToPortfolio || onFavorite || onAddToWatchlist);
          
          if (!hasHandlers) return null;
          
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onAddToPortfolio && (
                  <DropdownMenuItem onClick={() => onAddToPortfolio(fund)}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add to Portfolio
                  </DropdownMenuItem>
                )}
                {onFavorite && (
                  <DropdownMenuItem onClick={() => onFavorite(fund)}>
                    <Star className="mr-2 h-4 w-4" />
                    Favorite
                  </DropdownMenuItem>
                )}
                {onAddToWatchlist && (
                  <DropdownMenuItem onClick={() => onAddToWatchlist(fund)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Add to Watchlist
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        };
      }
    }
    
    return baseColumns;
  }, [onAddToPortfolio, onFavorite, onAddToWatchlist, bulkSelection, data]);

  // Table preferences for persistence
  const { loadPreferences, savePreferences } = useTablePreferences('funds-raw');
  
  // Handle responsive visibility
  useEffect(() => {
    const isSmallScreen = isSmallViewport(480);
    const newVisibility: VisibilityState = {
      company: !isSmallScreen || window.innerWidth >= 768,
      fundType: !isSmallScreen || window.innerWidth >= 768,
      strategy: !isSmallScreen || window.innerWidth >= 1024
    };
    setColumnVisibility(prev => ({
      ...prev,
      ...newVisibility
    }));
  }, []);

  // Load saved preferences on mount
  useEffect(() => {
    const prefs = loadPreferences();
    if (prefs) {
      if (prefs.columnVisibility) {
        setColumnVisibility(prev => ({
          ...prev,
          ...prefs.columnVisibility
        }));
      }
    }
  }, []);

  // Keep pagination in sync with props
  useEffect(() => {
    setPagination({
      pageIndex: currentPage - 1,
      pageSize: rowsPerPage
    });
  }, [currentPage, rowsPerPage]);

  // Save preferences when column visibility changes
  useEffect(() => {
    savePreferences({}, columnVisibility);
  }, [columnVisibility]);

  // Initialize table
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      pagination
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' 
        ? updater(pagination) 
        : updater;
        
      setPagination(newPagination);
      if (onPageChange) {
        onPageChange(newPagination.pageIndex + 1);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / rowsPerPage)
  });

  return (
    <div className="w-full rounded-md border">
      <div className="w-full overflow-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta as { className?: string } | undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "text-left sticky z-10 bg-muted/60 h-8 px-3 py-1",
                        "text-xs font-medium text-muted-foreground",
                        "transition-colors hover:bg-muted/70",
                        meta?.className,
                        header.column.getCanSort() ? "select-none cursor-pointer" : ""
                      )}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <AnimatePresence key={currentPage}>
              {table.getRowModel().rows.map((row, index) => (
              <motion.tr
                key={row.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
                className={cn(
                  "hover:bg-muted/20 transition-colors",
                  index % 2 === 0 ? "bg-background" : "bg-muted/40"
                )}
              >
                {row.getVisibleCells().map(cell => {
                  const meta = cell.column.columnDef.meta as { className?: string } | undefined;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "px-4 py-2 transition-colors",
                        meta?.className
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </motion.tr>
              ))}
            </AnimatePresence>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
